import URLSearchParams from '@ungap/url-search-params';
import { getApiHost } from 'base/api_host';
import { imageAssetSchema, imageAssetSchemaWithId } from 'base/asset';
import { HttpEngine } from 'base/auth_service';
import { delay, exists } from 'base/helpers';
import { productRestrictionsSchema } from 'base/product_restrictions';
import { generateRequestId, ServiceHttpError } from 'base/service_error';
import { zodParse } from 'base/zod_parse';
import { string, z } from 'zod';
import { projectDomain, ProjectState } from './editor/model';
import { AnyDelta, anyDeltaSchema } from './woot_woot/deltas';

export interface BlocksService {
  /* Public because the websocket needs the auth token */
  getUserToken: () => Promise<string>;

  //listProjects: () => Promise<ListProjectsResponse>;
  listProjectNames: () => Promise<ListProjectNamesResponse>;
  searchProjects(
    withPagination: boolean,
    ownershipType: OwnershipType,
    abortSignal: AbortSignal,
  ): Promise<SearchProjectsPageResponse>;
  getSearchProjectsPage(url: string): Promise<SearchProjectsPageResponse>;
  createProject: (req: CreateProjectRequest) => Promise<ProjectSummaryResponse>;
  projectGetSharingSettings(projectToken: string): Promise<SharingSettings>;
  projectUpdateSharingSettings(
    req: ProjectUpdateSharingSettingsRequest,
  ): Promise<SharingSettings>;
  projectGetOwner(projectToken: string): Promise<ProjectOwner>;
  projectDuplicate: (
    req: ProjectDuplicateRequest,
  ) => Promise<ProjectSummaryResponse>;
  projectRename(req: ProjectRenameRequest): Promise<ProjectSummaryResponse>;
  projectDelete: (req: ProjectDeleteRequest) => Promise<void>;
  legacyGetTokenForId: (
    req: LegacyGetTokenForIdRequest,
  ) => Promise<LegacyGetTokenForIdResponse>;

  projectSubmitComment: (
    req: ProjectSubmitCommentRequest,
  ) => Promise<ServerCommentMessage>;
  projectReadComment: (req: ProjectReadCommentRequest) => Promise<void>;
  projectUpdateComment: (
    req: ProjectUpdateCommentRequest,
  ) => Promise<ServerCommentMessage>;
  projectUpdateCommentThread: (
    req: ProjectUpdateCommentThreadRequest,
  ) => Promise<ServerCommentThreadMessage>;
  projectGetNotificationSubscription(
    projectToken: string,
  ): Promise<NotificationSubscription>;
  projectUpdateNotificationSubscription(
    req: ProjectUpdateNotificationSubscriptionRequest,
  ): Promise<NotificationSubscription>;

  sitemapImporterCrawl(
    rootDomain: string,
  ): AsyncIterable<CrawlPageEvent | CrawlStatusEvent>;
  getImages(
    projectToken: string,
    ids: number[],
  ): Promise<z.infer<typeof getImageResponse>>;
  uploadImage(
    projectToken: string,
    file: File,
  ): Promise<z.infer<typeof imageUploadResponse>>;

  getFontFamily(slug: string): Promise<FontFamilyMetadata>;
  searchFontFamilies(
    query: string,
    tags: string[],
    abortSignal: AbortSignal,
    includePremiumFonts?: boolean,
  ): Promise<FontFamilyPagedResponse>;
  getSearchFontFamiliesPage(url: string): Promise<FontFamilyPagedResponse>;
  listRecommendedFontFamilies(
    likedFonts: ReadonlyArray<string>,
    count?: number,
    includePremiumFonts?: boolean,
  ): Promise<ListRecommendedFontFamiliesResponse>;
  listFontFamiliesFromCollections(
    collections: ReadonlyArray<string>,
    count?: number,
    tags?: string[],
  ): Promise<ListFontFamiliesFromCollectionsResponse>;
  getFontCollection(slug: string): Promise<FontCollection>;

  projectSubmitAppFeedback(
    req: ProjectSubmitAppFeedbackRequest,
  ): Promise<ProjectSubmitAppFeedbackResponse>;
}

export class HttpBlocksService implements BlocksService {
  constructor(private readonly http: HttpEngine) {}

  getUserToken() {
    return this.http.getTokenLegacy();
  }

  /**
   * Returns a list of ALL project names for the current user.
   */
  async listProjectNames(): Promise<ListProjectNamesResponse> {
    return this.http.request(
      'GET',
      `/blocks/projects/names`,
      undefined,
      listProjectNamesResponseSchema,
    );
  }
  /**
   * Returns the first page of a search, the response contains a "next" url that
   * can be used with getSearchFontFamiliesPage to query additional pages of search
   * results.
   */
  async searchProjects(
    withPagination: boolean,
    ownershipType: OwnershipType,
    abortSignal: AbortSignal,
  ) {
    const params = new URLSearchParams();
    params.append('ownerType', ownershipType);
    if (withPagination) {
      params.append('withPagination', 'true');
    }
    const r = await this.http.request(
      'GET',
      `/blocks/projects?${params}`,
      undefined,
      searchProjectsPageResponseSchema,
      abortSignal,
    );
    return r;
  }

  getSearchProjectsPage(url: string) {
    const parsedUrl = new URL(url);
    return this.http.request(
      'GET',
      parsedUrl.pathname + parsedUrl.search,
      undefined,
      searchProjectsPageResponseSchema,
    );
  }

  async createProject(
    req: CreateProjectRequest,
  ): Promise<ProjectSummaryResponse> {
    return this.http.request(
      'POST',
      `/blocks/projects/create`,
      req,
      projectSummaryResponseSchema,
    );
  }

  async projectGetSharingSettings(
    projectToken: string,
  ): Promise<SharingSettings> {
    return this.http.request(
      'GET',
      `/blocks/project/sharing_settings?projectToken=${projectToken}`,
      undefined,
      sharingSettingsSchema,
    );
  }

  async projectUpdateSharingSettings(
    req: ProjectUpdateSharingSettingsRequest,
  ): Promise<SharingSettings> {
    return this.http.request(
      'POST',
      `/blocks/project/sharing_settings`,
      req,
      sharingSettingsSchema,
    );
  }

  async projectGetOwner(projectToken: string): Promise<ProjectOwner> {
    return this.http.request(
      'GET',
      `/blocks/project/owner?projectToken=${projectToken}`,
      undefined,
      projectOwnerSchema,
    );
  }

  async projectDuplicate(
    req: ProjectDuplicateRequest,
  ): Promise<ProjectSummaryResponse> {
    return this.http.request(
      'POST',
      `/blocks/project/duplicate`,
      req,
      projectSummaryResponseSchema,
    );
  }

  async projectRename(
    req: ProjectRenameRequest,
  ): Promise<ProjectSummaryResponse> {
    return this.http.request(
      'POST',
      `/blocks/project/rename`,
      req,
      projectSummaryResponseSchema,
    );
  }

  async projectDelete(req: ProjectDeleteRequest): Promise<void> {
    await this.http.request('POST', `/blocks/project/delete`, req);
  }

  async legacyGetTokenForId(
    req: LegacyGetTokenForIdRequest,
  ): Promise<LegacyGetTokenForIdResponse> {
    return this.http.request(
      'POST',
      `/blocks/legacy_get_token_for_id`,
      req,
      legacyGetTokenForIdResponseSchema,
    );
  }

  async projectSubmitComment(
    req: ProjectSubmitCommentRequest,
  ): Promise<ServerCommentMessage> {
    return this.http.request(
      'POST',
      `/blocks/project/submit_comment`,
      req,
      serverCommentMessageSchema,
    );
  }

  async projectReadComment(req: ProjectReadCommentRequest): Promise<void> {
    await this.http.request('POST', `/blocks/project/read_comment`, req);
  }

  async projectUpdateComment(
    req: ProjectUpdateCommentRequest,
  ): Promise<ServerCommentMessage> {
    return this.http.request(
      'POST',
      `/blocks/project/update_comment`,
      req,
      serverCommentMessageSchema,
    );
  }

  async projectUpdateCommentThread(
    req: ProjectUpdateCommentThreadRequest,
  ): Promise<ServerCommentThreadMessage> {
    return this.http.request(
      'POST',
      `/blocks/project/update_comment_thread`,
      req,
      serverCommentThreadMessageSchema,
    );
  }

  async projectGetNotificationSubscription(
    projectToken: string,
  ): Promise<NotificationSubscription> {
    return this.http.request(
      'GET',
      `/blocks/project/notification_subscription?projectToken=${projectToken}`,
      undefined,
      notificationSubscriptionSchema,
    );
  }

  async projectUpdateNotificationSubscription(
    req: ProjectUpdateNotificationSubscriptionRequest,
  ): Promise<NotificationSubscription> {
    return this.http.request(
      'POST',
      `/blocks/project/notification_subscription`,
      req,
      notificationSubscriptionSchema,
    );
  }

  async *sitemapImporterCrawl(
    rootDomain: string,
  ): AsyncIterable<CrawlPageEvent | CrawlStatusEvent> {
    const requestId = generateRequestId();
    const url = `${getApiHost()}blocks/sitemap_importer/crawl`;
    const token = await this.getUserToken();
    const res = await fetch(url, {
      method: 'POST',
      headers: [
        ['x-request-id', requestId],
        ['authorization', `Bearer ${token}`],
        ['content-type', 'application/json'],
      ],
      body: JSON.stringify({
        rootDomain,
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new ServiceHttpError(res.status, text, requestId);
    }
    if (!res.body) throw new Error('no response body');

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = '';
    while (true) {
      const { value, done } = await Promise.race([
        reader.read(),
        // If there's been no data for 60 seconds, assume the connection is dead
        new Promise<never>((_, reject) =>
          delay(60_000).then(() =>
            reject(new CrawlError('Connection with the server was lost.')),
          ),
        ),
      ]);
      const parseResult = parseEvents(buffer + value);
      buffer = parseResult.rest;
      for (const eventString of parseResult.events) {
        const eventRaw = JSON.parse(eventString);
        const event = zodParse(crawlEventSchema, eventRaw);
        if (event.type === 'error') {
          throw new CrawlError(event.message);
        }
        yield event;
      }
      if (done) {
        break;
      }
    }
  }

  async uploadImage(projectToken: string, file: File) {
    const data = new FormData();
    data.append('0', file);
    data.append('projectToken', projectToken);
    return this.http.requestRaw(
      'POST',
      `blocks/project/images`,
      data,
      [],
      imageUploadResponse,
    );
  }

  async getImages(projectToken: string, ids: number[]) {
    const params = new URLSearchParams([['projectToken', projectToken]]);
    ids.forEach((id) => params.append('id', `${id}`));
    return this.http.request(
      'GET',
      `blocks/project/images?${params}`,
      undefined,
      getImageResponse,
    );
  }

  /**
   * Returns the first page of a search, the response contains a "next" url that
   * can be used with getSearchFontFamiliesPage to query additional pages of search
   * results.
   */
  async searchFontFamilies(
    query: string,
    tags: string[],
    abortSignal: AbortSignal,
    includePremiumFonts?: boolean,
  ) {
    const r = await this.http.request(
      'GET',
      `/v1/font_families/search?query=${encodeURIComponent(
        query,
      )}&tags=${encodeURIComponent(tags.join(','))}&includePremiumFonts=${(
        includePremiumFonts ?? false
      ).toString()}`,
      undefined,
      fontFamilyPagedResponseSchema,
      abortSignal,
    );
    return r;
  }

  getSearchFontFamiliesPage(url: string) {
    const parsedUrl = new URL(url);
    return this.http.request(
      'GET',
      parsedUrl.pathname + parsedUrl.search,
      undefined,
      fontFamilyPagedResponseSchema,
    );
  }

  getFontFamily(slug: string) {
    return this.http.request(
      'GET',
      `/v1/font_family/${encodeURIComponent(slug)}`,
      undefined,
      fontFamilyMetadataSchema,
    );
  }

  listRecommendedFontFamilies(
    likedFonts: ReadonlyArray<string>,
    count?: number,
    includePremiumFonts?: boolean,
  ): Promise<ListRecommendedFontFamiliesResponse> {
    const params = new URLSearchParams();
    params.append('liked', likedFonts.join(','));
    if (count !== undefined) {
      params.append('count', count.toString());
    }
    params.append(
      'includePremiumFonts',
      (includePremiumFonts ?? false).toString(),
    );

    params.append('deterministic', '1');
    return this.http.request(
      'GET',
      `/v1/font_families/recommended?${params}`,
      undefined,
      listRecommendedFontFamiliesResponseSchema,
    );
  }

  listFontFamiliesFromCollections(
    collections: ReadonlyArray<string>,
    count?: number,
    tags?: string[],
  ) {
    const params = new URLSearchParams();
    params.append('collections', collections.join(','));
    if (count !== undefined) {
      params.append('count', count.toString());
    }
    if (tags !== undefined) {
      params.append('tags', tags.join(','));
    }
    return this.http.request(
      'GET',
      `/v1/font_families/from_collections?${params}`,
      undefined,
      listFontFamiliesFromCollectionsResponseSchema,
    );
  }

  getFontCollection(slug: string): Promise<FontCollection> {
    return this.http.request(
      'GET',
      `/v1/font_collection/${slug}`,
      undefined,
      fontCollectionSchema,
    );
  }

  projectSubmitAppFeedback(
    req: ProjectSubmitAppFeedbackRequest,
  ): Promise<ProjectSubmitAppFeedbackResponse> {
    return this.http.request(
      'POST',
      `/blocks/project/app_feedback`,
      req,
      projectSubmitAppFeedbackResponseSchema,
    );
  }
}

type CreateProjectRequest = {
  data: ProjectState;
  owner: OwnershipType;
};
export type OwnershipType = 'user' | 'team' | 'all';

export type ProjectUpdateSharingSettingsRequest = {
  projectToken: string;
  linkAccessLevel?: AccessLevel;
  ownershipType?: OwnershipType;
};

type ProjectDuplicateRequest = {
  projectToken: string;
};

type ProjectRenameRequest = {
  projectToken: string;
  name: string;
};

type ProjectDeleteRequest = {
  projectToken: string;
};

type LegacyGetTokenForIdRequest = {
  projectId: number | string;
};

type ProjectSubmitCommentRequest = {
  projectToken: string;
  content: string;
  thread: number | { new: true };
};

type ProjectReadCommentRequest = {
  projectToken: string;
  commentId: number;
  isRead: boolean;
};

type ProjectUpdateCommentRequest = {
  projectToken: string;
  commentId: number;
  content?: string;
  deleted?: boolean;
};

type ProjectUpdateCommentThreadRequest = {
  projectToken: string;
  threadId: number;
  resolved?: boolean;
  deleted?: boolean;
};

type ProjectUpdateNotificationSubscriptionRequest = {
  projectToken: string;
  preference: NotificationSubscription['preference'];
};

type ProjectSubmitAppFeedbackRequest = {
  projectToken: string;
  type: 'general' | 'bug';
  page: 'styleguide' | 'webflow_app' | 'figma_app';
  message: string;
  concept: number;
};

const legacyGetTokenForIdResponseSchema = z.object({
  token: z.string(),
});
type LegacyGetTokenForIdResponse = z.infer<
  typeof legacyGetTokenForIdResponseSchema
>;

const commonFlagsSchema = z.object({
  // Long term flags
  hasPaymentProblem: z.boolean().default(false),
  teamFeaturesRequireUpgrade: z.boolean().default(false),
});
export type CommonFlags = z.infer<typeof commonFlagsSchema>;

export const featureFlagsSchema = commonFlagsSchema.extend({
  // Long term flags
  canExportComponents: z.boolean().default(false),
  showUpgradeButton: z.boolean().default(false),
  enableDevTools: z.boolean().default(false),
  useStagingComponents: z.boolean().default(false),
  enableReactExportDebugging: z.boolean().default(false),
  enablePromptOverrides: z.boolean().default(false),

  // Short term flags
  enableDesignView: z.boolean().default(false),
  enableMonotypeFonts: z.boolean().default(true),

  // posthog
  enableReferral: z.boolean().default(false),
  enableCanvasCursorOptimization: z.boolean().default(false),
});
export type FeatureFlags = z.infer<typeof featureFlagsSchema>;
export const DEFAULT_FEATURE_FLAGS = zodParse(featureFlagsSchema, {});

const editorConfigSchema = z.object({
  pageLimit: z.number().nullable(),
});
export type EditorConfig = z.infer<typeof editorConfigSchema>;

export type ClientMessage =
  | {
      type: 'ping';
    }
  | {
      type: 'init';
      schemaVersion: number;
      userToken: string | undefined;
      projectToken: string;
      baseVersion?: number;
      oldSessionIds: number[];
      hideFromPresence: boolean;
    }
  | {
      type: 'submitDelta';
      data: readonly AnyDelta[];
    }
  | {
      type: 'presenceDecoration';
      key: string;
      value: string | undefined;
    };

const serverDeltaMessageSchema = z.object({
  type: z.literal('delta'),
  id: z.number(),
  data: z.array(anyDeltaSchema),
});
export enum AccessLevel {
  NONE = 0,
  VIEWER = 100,
  COMMENTER = 200,
  OWNER = 500,
}
const accessLevelSchema = z.nativeEnum(AccessLevel);
const userAvatarSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  color: z.string().regex(/^#[0-9a-f]{6}$/),
  avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
});
export type UserAvatar = z.infer<typeof userAvatarSchema>;
const commentSchema = z.object({
  id: z.number(),
  version: z.number(),
  threadId: z.number(),
  content: z.string(),
  createdAt: z.string(),
  deleted: z.boolean(),
  author: userAvatarSchema,
  isRead: z.boolean(),
});
export type Comment = z.infer<typeof commentSchema>;
const commentThreadSchema = z.object({
  id: z.number(),
  version: z.number(),
  resolved: z.boolean(),
  deleted: z.boolean(),
});
export type CommentThread = z.infer<typeof commentThreadSchema>;
const serverCommentMessageSchema = z.object({
  type: z.literal('comment'),
  comment: commentSchema,
  thread: commentThreadSchema,
});
export type ServerCommentMessage = z.infer<typeof serverCommentMessageSchema>;
const serverCommentThreadMessageSchema = z.object({
  type: z.literal('commentThread'),
  thread: commentThreadSchema,
});
export type ServerCommentThreadMessage = z.infer<
  typeof serverCommentThreadMessageSchema
>;
const serverPresenceJoinMessageSchema = z.object({
  type: z.literal('presenceJoin'),
  sessionId: z.number(),
  color: z.string().regex(/^#[0-9a-f]{6}$/),
  user: userAvatarSchema.nullish().transform((x) => x ?? undefined),
  accessLevel: accessLevelSchema,
});
export type ServerPresenceJoinMessage = z.infer<
  typeof serverPresenceJoinMessageSchema
>;
const serverPresenceDecorationMessageSchema = z.object({
  type: z.literal('presenceDecoration'),
  sessionId: z.number(),
  key: z.string(),
  value: z.string().nullable(),
});
export type ServerPresenceDecorationMessage = z.infer<
  typeof serverPresenceDecorationMessageSchema
>;
const serverInitMessageSchema = z.object({
  type: z.literal('init'),
  schemaVersion: z.number().optional(),
  sessionId: z.number(),
  baseVersion: z.number(),
  initialState: projectDomain.stateSchema,
  initialDeltas: serverDeltaMessageSchema.array(),
  initialComments: commentSchema.array(),
  initialCommentThreads: commentThreadSchema.array(),
  initialPresenceJoins: serverPresenceJoinMessageSchema.array(),
  initialPresenceDecorations: serverPresenceDecorationMessageSchema.array(),
  editorConfig: editorConfigSchema,
  editorFlags: featureFlagsSchema,
  accessLevel: accessLevelSchema,
  analyticsContext: z.object({}).passthrough(),
  productRestrictions: productRestrictionsSchema,
  locked: z.boolean(),
  createdBy: z.number(),
  createdAt: z.string(),
});
export type ServerInitMessage = z.infer<typeof serverInitMessageSchema>;
export const serverMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('pong'),
  }),
  serverInitMessageSchema,
  serverDeltaMessageSchema,
  serverCommentMessageSchema,
  serverCommentThreadMessageSchema,
  serverPresenceJoinMessageSchema,
  serverPresenceDecorationMessageSchema,
  z.object({
    type: z.literal('presenceLeave'),
    sessionId: z.number(),
  }),
  z
    .object({
      type: z.literal('error'),
      statusCode: z.number(),
    })
    .passthrough(),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;

const projectSummaryResponseSchema = z.object({
  token: z.string(),
  version: z.number(),
});
export type ProjectSummaryResponse = z.infer<
  typeof projectSummaryResponseSchema
>;

const sharingSettingsSchema = z.object({
  token: z.string(),
  ownershipType: z.enum(['user', 'team']),
  linkAccessLevel: accessLevelSchema,
  canUpdateSharingSettings: z.boolean(),
  teamName: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
});
export type SharingSettings = z.infer<typeof sharingSettingsSchema>;

const projectOwnerSchema = z.object({
  team: z
    .object({
      name: z.string(),
      avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
      logo: imageAssetSchema.nullish().transform((x) => x ?? undefined),
    })
    .nullable(),
  user: z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
      color: string().regex(/^#[0-9a-f]{6}$/),
    })
    .nullable(),
  creator: z.object({
    firstName: z.string(),
    lastName: z.string(),
    avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
    color: string().regex(/^#[0-9a-f]{6}$/),
  }),
});

export type ProjectOwner = z.infer<typeof projectOwnerSchema>;

const notificationSubscriptionSchema = z.object({
  email: z.string(),
  preference: z.enum(['none', 'all', 'replies-only']),
});
export type NotificationSubscription = z.infer<
  typeof notificationSubscriptionSchema
>;

const listProjectsResponseSchema = z.object({
  projects: z.array(
    z.object({
      token: z.string(),
      ownershipType: z.enum(['user', 'team']),
      team: z
        .object({
          name: z.string(),
          color: z.string().regex(/^#[0-9a-f]{6}$/),
          avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
        })
        .nullable(),
      name: z
        .string()
        .nullish()
        .transform((x) => x ?? undefined),
      updatedAt: z.number(),
      currentUserLastViewedAt: z.number().nullable(),
      createdByCurrentUser: z.boolean().nullable(),
      sessions: z.array(serverPresenceJoinMessageSchema).default([]),
      locked: z.boolean(),
    }),
  ),
  projectsQuota: z.object({
    // nullish means unlimited
    limit: z
      .number()
      .nullish()
      .transform((x) => x ?? undefined),
    used: z.number(),
  }),
  teamName: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  flags: commonFlagsSchema,
});
export type ListProjectsResponse = z.infer<typeof listProjectsResponseSchema>;

const listProjectNamesResponseSchema = z.object({
  projects: z.array(
    z.object({
      token: z.string(),
      ownershipType: z.enum(['user', 'team']),
      name: z
        .string()
        .nullish()
        .transform((x) => x ?? undefined),
      updatedAt: z.number(),
    }),
  ),
});
export type ListProjectNamesResponse = z.infer<
  typeof listProjectNamesResponseSchema
>;

const projectInfoSchema = z.object({
  token: z.string(),
  ownershipType: z.enum(['user', 'team']),
  team: z
    .object({
      name: z.string(),
      color: z.string().regex(/^#[0-9a-f]{6}$/),
      avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
    })
    .nullable(),
  name: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  updatedAt: z.number(),
  currentUserLastViewedAt: z.number().nullable(),
  createdByCurrentUser: z.boolean().nullable(),
  sessions: z.array(serverPresenceJoinMessageSchema).default([]),
  locked: z.boolean(),
});
export type ProjectInfo = z.infer<typeof projectInfoSchema>;

const searchProjectsPageResponseSchema = z.object({
  count: z.number().optional(),
  next: z.string().nullable().optional(),
  previous: z.string().nullable().optional(),
  projects: z.array(projectInfoSchema),
  projectsQuota: z.object({
    // nullish means unlimited
    limit: z
      .number()
      .nullish()
      .transform((x) => x ?? undefined),
    used: z.number(),
  }),
  teamName: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  flags: commonFlagsSchema,
});
export type SearchProjectsPageResponse = z.infer<
  typeof searchProjectsPageResponseSchema
>;

const crawlPageEventSchema = z.object({
  type: z.literal('page'),
  path: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable(),
});
export type CrawlPageEvent = z.infer<typeof crawlPageEventSchema>;

const crawlStatusEventSchema = z.object({
  type: z.literal('status'),
  currentUrl: z.string(),
  currentPage: z.number(),
  totalPages: z.number(),
  pageLimitReached: z.boolean(),
});
type CrawlStatusEvent = z.infer<typeof crawlStatusEventSchema>;

const crawlErrorEventSchema = z.object({
  type: z.literal('error'),
  message: z.string(),
});

const crawlEventSchema = z.union([
  crawlPageEventSchema,
  crawlStatusEventSchema,
  crawlErrorEventSchema,
]);

export class CrawlError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CrawlError';
  }
}

const imageUploadResponse = z.object({
  assets: z.array(imageAssetSchemaWithId),
});

const getImageResponse = z.object({
  assets: z.array(imageAssetSchemaWithId),
});

export const fontFaceMetadataSchema = z.object({
  slug: z.string(),
  style: z.string(),
  weight: z.string(),
  name: z.string(),
  price: z.string().optional().nullable(),
  acquisitionUrl: z.string().optional().nullable(),
});
export type FontFaceMetadata = z.infer<typeof fontFaceMetadataSchema>;

const fontFamilyMetadataSchema = z.object({
  slug: z.string(),
  name: z.string(),
  source: z.union([z.literal('G'), z.literal('M')]),
  previewUrl: z.string(),
  acquisitionUrl: z.string(),
  fontFaces: z.array(fontFaceMetadataSchema),
});
export type FontFamilyMetadata = z.infer<typeof fontFamilyMetadataSchema>;

const fontFamilyPagedResponseSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(fontFamilyMetadataSchema),
});
export type FontFamilyPagedResponse = z.infer<
  typeof fontFamilyPagedResponseSchema
>;

const listRecommendedFontFamiliesResponseSchema = z.array(
  z.object({
    fontFamily: fontFamilyMetadataSchema,
    similarTo: z.string(),
  }),
);
export type ListRecommendedFontFamiliesResponse = z.infer<
  typeof listRecommendedFontFamiliesResponseSchema
>;

const listFontFamiliesFromCollectionsResponseSchema = z.array(
  z.object({
    fontFamily: fontFamilyMetadataSchema,
    collection: z.string(),
  }),
);
export type ListFontFamiliesFromCollectionsResponse = z.infer<
  typeof listFontFamiliesFromCollectionsResponseSchema
>;

const fontCollectionSchema = z.object({
  fontFamilies: z.array(fontFamilyMetadataSchema),
});
export type FontCollection = z.infer<typeof fontCollectionSchema>;

const projectSubmitAppFeedbackResponseSchema = z.object({
  success: z.boolean(),
});
export type ProjectSubmitAppFeedbackResponse = z.infer<
  typeof projectSubmitAppFeedbackResponseSchema
>;

// TODO: dedupe with llm_proxy
function parseEvents(buffer: string): {
  events: string[];
  rest: string;
} {
  const lines = buffer.split('\n');
  const rest = lines.pop() || '';
  const events = lines
    .map((l) => {
      l = l.trim();
      if (l.length == 0) return;
      if (l.startsWith('data: ')) {
        return l.substring(6);
      }
      throw new Error(`unknown SSE line: ${l}`);
    })
    .filter(exists);
  return { events, rest };
}
